const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

export interface Debin {
  id:               string;
  fecha:            string;
  vencimiento:      string;
  razon_social:     string;
  cuit_originante:  string;
  cbu_originante:   string;
  cuit_comprador:   string;
  cbu_destino:      string;
  collector:        string;   // colector(es) de la whitelist para la CVU destino; fallback: titular del comprador (Coelsa)
  moneda:           "ARS" | "USD" | string;
  importe:          number;
  estado:           string;
}

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") {
      window.location.replace("/login");
    }
  }
  return res;
}

/** El ambiente Coelsa (homo|prd) lo fija el deployment (sandbox → homo, prd →
 *  prd): el backend lo informa en la respuesta, la UI ya no lo elige. */
export type Ambiente = "homo" | "prd";

export async function listDebines(
  opts?: { from?: string; to?: string },
): Promise<{ items: Debin[]; total: number; synced_at?: string; ambiente?: Ambiente }> {
  const qs = new URLSearchParams();
  if (opts?.from) qs.set("from", opts.from);
  if (opts?.to)   qs.set("to",   opts.to);
  const res = await authFetch(`${API_BASE}/debines${qs.toString() ? `?${qs}` : ""}`, { headers: authHeader() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `listDebines: ${res.status}`);
  }
  return res.json();
}

/** Actualización A DEMANDA del cache de DEBINes: dispara el sync contra Coelsa
 *  del ambiente del deployment (async). Devuelve el synced_at previo — el caller pollea
 *  listDebines hasta que cambie (el sync tarda ~15-30 s). */
export async function syncDebines(): Promise<{ prev_synced_at: string }> {
  const res = await authFetch(`${API_BASE}/debines/sync`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `syncDebines: ${res.status}`);
  return { prev_synced_at: String(data.prev_synced_at ?? "") };
}

/** CVUs habilitados para débito (tabla debin_debit_accounts). Se usa para
 *  ocultar el botón Aprobar de los DEBINes cuyo CVU destino no esté habilitado. */
export async function listDebinWhitelist(): Promise<string[]> {
  const res = await authFetch(`${API_BASE}/debines/whitelist`, { headers: authHeader() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `listDebinWhitelist: ${res.status}`);
  }
  const data = await res.json();
  return (data.accounts ?? []) as string[];
}

// ----- ABM de whitelist (página Whitelist) -----------------------------------
// Un CVU puede tener MÁS de un colector (collectors[]).

export interface WhitelistItem {
  cvu:         string;
  collectors:  string[];
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface CollectorOption {
  name:   string;  // nombre del PRODUCT en collection_config
  tax_id: string;  // CUIT del colector (formato con guiones)
}

export async function listWhitelistItems(): Promise<WhitelistItem[]> {
  const res = await authFetch(`${API_BASE}/debines/whitelist`, { headers: authHeader() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `listWhitelistItems: ${res.status}`);
  }
  const data = await res.json();
  return (data.items ?? []) as WhitelistItem[];
}

/** Colectores disponibles (collection_config, PRODUCT# con tax_id) para el
 *  desplegable con búsqueda del alta. */
export async function listCollectors(): Promise<CollectorOption[]> {
  const res = await authFetch(`${API_BASE}/debines/collectors`, { headers: authHeader() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `listCollectors: ${res.status}`);
  }
  const data = await res.json();
  return (data.items ?? []) as CollectorOption[];
}

export async function addWhitelistEntry(cvu: string, collector: string): Promise<WhitelistItem> {
  const res = await authFetch(`${API_BASE}/debines/whitelist`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ cvu, collector }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `addWhitelistEntry: ${res.status}`);
  return data as WhitelistItem;
}

// ----- ABM de whitelist de cuentas de CRÉDITO (tabla debin_credit_accounts) --
// Un CBU/CVU tiene UN `owner` (etiqueta libre del titular, no un colector).

export interface CreditAccount {
  cbu:         string;
  owner:       string;
  updated_at?: string | null;
  updated_by?: string | null;
}

export async function listCreditAccounts(): Promise<CreditAccount[]> {
  const res = await authFetch(`${API_BASE}/debines/credit-whitelist`, { headers: authHeader() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `listCreditAccounts: ${res.status}`);
  }
  const data = await res.json();
  return (data.items ?? []) as CreditAccount[];
}

/** Upsert: re-agregar un CBU existente actualiza su titular. */
export async function addCreditAccount(cbu: string, owner: string): Promise<CreditAccount> {
  const res = await authFetch(`${API_BASE}/debines/credit-whitelist`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ cbu, owner }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `addCreditAccount: ${res.status}`);
  return data as CreditAccount;
}

export async function removeCreditAccount(cbu: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/debines/credit-whitelist/${encodeURIComponent(cbu)}`, {
    method:  "DELETE",
    headers: authHeader(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `removeCreditAccount: ${res.status}`);
  }
}

/** Sin `collector` borra el CVU entero; con `collector` saca solo ese colector. */
export async function removeWhitelistEntry(cvu: string, collector?: string): Promise<void> {
  const qs = collector ? `?collector=${encodeURIComponent(collector)}` : "";
  const res = await authFetch(`${API_BASE}/debines/whitelist/${encodeURIComponent(cvu)}${qs}`, {
    method:  "DELETE",
    headers: authHeader(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `removeWhitelistEntry: ${res.status}`);
  }
}

// ----- Rastreo por ID Coelsa (página /monitoreo/id-coelsa) -------------------

export interface DebinTraceLogs {
  window?: { from: string; to: string };
  [group: string]: any;   // webadmin2 / coelsa_connector → {events: [{t,m}], truncated} | {error}
}

export interface DebinTrace {
  id:           string;
  found_any:    boolean;
  cache:        Record<string, any>;   // por ambiente (prd/homo): fila del listado Coelsa
  acreditacion: any | null;            // debin_acreditaciones: acreditación bancaria real
  audit:        any[];                 // webadmin2-debin-audit: acciones de operadores
  movimientos:  any[];                 // cmf_movimientos: movimiento(s) bancario(s) crudo(s)
  // CP-155: cobros por transferencia a CVU (received_payments, GSI por
  // network_trace_id = ID de transacción BIND). El mismo campo `id` de la
  // búsqueda puede ser un ID Coelsa (DEBIN) o un ID BIND (transferencia).
  transferencia_bind: any[];
  // CP-517: transferencias SALIENTES (liquidaciones / devoluciones de ar_operations)
  // por el id que dio el banco: CMF o STP (clave de rastreo / orden), por índice.
  salientes?: any[];
  logs:         DebinTraceLogs;
}

/** Junta todo lo que el ecosistema sabe de un ID (DEBIN, transferencia BIND o saliente). */
export async function traceDebin(id: string): Promise<DebinTrace> {
  const qs = new URLSearchParams({ id });
  const res = await authFetch(
    `${API_BASE}/monitoreo/id-coelsa?${qs}`,
    { headers: authHeader() },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `traceDebin: ${res.status}`);
  return data as DebinTrace;
}

/**
 * Genera el comprobante PDF de un DEBIN acreditado y devuelve un Blob URL para
 * descargarlo/abrirlo. El backend lo renderiza con toda la info de la fila
 * (que ya tenemos), así que se la mandamos en el body. El caller debe hacer
 * `URL.revokeObjectURL(url)` cuando termine.
 */
export async function getDebinComprobanteUrl(debin: Debin): Promise<string> {
  const res = await authFetch(
    `${API_BASE}/debines/${encodeURIComponent(debin.id)}/comprobante`,
    {
      method:  "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body:    JSON.stringify(debin),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `comprobante DEBIN → ${res.status}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function confirmDebin(
  id: string,
  importe: number,
  cbu_comprador: string,
  cuit_comprador: string,
  cuit_vendedor: string,
  cbu_vendedor: string,
): Promise<void> {
  const res = await authFetch(`${API_BASE}/debines/${encodeURIComponent(id)}/confirm`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ importe, cbu_comprador, cuit_comprador, cuit_vendedor, cbu_vendedor }),
  });
  if (!res.ok) throw new Error(`confirmDebin HTTP ${res.status}`);
  const data = await res.json();
  if (!data.ok) throw new Error(`Coelsa: ${data.coelsa?.descripcion ?? JSON.stringify(data.coelsa)}`);
}

export async function rejectDebin(
  id: string,
  importe: number,
  cbu_comprador: string,
  cuit_comprador: string,
  cuit_vendedor: string,
  cbu_vendedor: string,
): Promise<void> {
  const res = await authFetch(`${API_BASE}/debines/${encodeURIComponent(id)}/reject`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body:    JSON.stringify({ importe, cbu_comprador, cuit_comprador, cuit_vendedor, cbu_vendedor }),
  });
  if (!res.ok) throw new Error(`rejectDebin HTTP ${res.status}`);
  const data = await res.json();
  if (!data.ok) throw new Error(`Coelsa: ${data.coelsa?.descripcion ?? JSON.stringify(data.coelsa)}`);
}
